/**
 * SigningPage — public SPA route for external signers (Phase 4 R1).
 *
 * Reached via the URL emitted by the backend signing-email dispatch:
 *
 *     {SIGN_URL_BASE}/sign/{review_id_short}.{raw_token}
 *
 * No Firebase auth. Authenticates the signer via the signing_token. The page
 * previews the PDF, surfaces the signer + consent text, and on confirm calls
 * the existing POST /api/sign/{review_id}/sign-external (which is what
 * consumes the token).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileText, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PortalActionChrome } from '@/components/portal/PortalActionChrome';

interface SignLinkMeta {
  review_id: string;
  review: {
    id: string;
    status: string;
    mode: string;
    file_ref: string;
    consent_text: string;
    project_name?: string | null;
    document_title?: string | null;
  };
  signer: {
    id: string;
    email: string;
    role?: string | null;
    position?: number;
    signed_at?: string | null;
  };
  branding?: {
    logo_url?: string | null;
    company_name?: string | null;
    primary_color?: string | null;
  } | null;
}

interface PreviewPage {
  page_number: number;
  image_data_uri: string;
}

interface PreviewPagesResponse {
  pages: PreviewPage[];
  page_count: number;
  review_title: string | null;
  truncated_at_cap: boolean;
  max_page_cap: number;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) ?? '';

export function SigningPage() {
  const { signRef } = useParams<{ signRef: string }>();

  const [reviewIdShort, rawToken] = useMemo(() => {
    if (!signRef) return ['', ''];
    // URL pattern: {12 hex chars}.{raw_token}
    const idx = signRef.indexOf('.');
    if (idx === -1) return [signRef, ''];
    return [signRef.slice(0, idx), signRef.slice(idx + 1)];
  }, [signRef]);

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<SignLinkMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typedName, setTypedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  // PDF preview pages (server-rendered JPEGs) — replaces the v0.13 iframe
  // which rendered blank under Firefox + headless Chromium + had inconsistent
  // behavior across browsers. See backend /preview-pages endpoint.
  const [previewPages, setPreviewPages] = useState<PreviewPage[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [previewTotalCount, setPreviewTotalCount] = useState(0);
  const [previewRetryCount, setPreviewRetryCount] = useState(0);

  useEffect(() => {
    if (!reviewIdShort || !rawToken) {
      setError('Invalid signing link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/sign/by-link/${encodeURIComponent(reviewIdShort)}/${encodeURIComponent(rawToken)}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const detail = typeof body.detail === 'string'
            ? body.detail
            : (body.detail?.message ?? 'This signing link is no longer valid.');
          if (!cancelled) setError(detail);
        } else {
          const data = (await res.json()) as SignLinkMeta;
          if (!cancelled) {
            setMeta(data);
            if (data.signer.signed_at) setSignedAt(data.signer.signed_at);
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load signing link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewIdShort, rawToken]);

  async function handleSign() {
    if (!meta || !rawToken) return;
    if (!consentChecked) return;
    setSigning(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/sign/${encodeURIComponent(meta.review_id)}/sign-external`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            raw_token: rawToken,
            type: 'signature',
            page_number: 1,
            coords: { x: 50, y: 50 },
            annotation_data: {
              typed_name: typedName.trim() || meta.signer.email,
              consent_accepted: true,
            },
          }),
        },
      );
      if (!res.ok && res.status !== 202) {
        const body = await res.json().catch(() => ({}));
        const detail = typeof body.detail === 'string'
          ? body.detail
          : (body.detail?.message ?? 'Failed to record signature.');
        setError(detail);
      } else {
        setSignedAt(new Date().toISOString());
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to record signature.');
    } finally {
      setSigning(false);
    }
  }

  // Fetch the server-rendered preview pages once the meta + token are valid.
  // Failure here is NON-blocking — the consent + sign path stays usable; the
  // preview pane shows an explicit "preview unavailable" state with retry.
  useEffect(() => {
    if (!reviewIdShort || !rawToken || !meta) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/sign/by-link/${encodeURIComponent(reviewIdShort)}/${encodeURIComponent(rawToken)}/preview-pages`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const detail = typeof body.detail === 'string'
            ? body.detail
            : (body.detail?.message ?? 'Could not load document preview.');
          if (!cancelled) setPreviewError(detail);
        } else {
          const data = (await res.json()) as PreviewPagesResponse;
          if (!cancelled) {
            setPreviewPages(data.pages);
            setPreviewTruncated(data.truncated_at_cap);
            setPreviewTotalCount(data.page_count);
          }
        }
      } catch (err: any) {
        if (!cancelled) setPreviewError(err?.message ?? 'Could not load document preview.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewIdShort, rawToken, meta, previewRetryCount]);

  const retryPreview = useCallback(() => {
    setPreviewRetryCount((n) => n + 1);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading signing link…</span>
        </div>
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow border border-slate-200 p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-1">Signing link not available</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) return null;

  // Theme-3 iter-9 item 4: SigningPage header + success card both wrap in
  // PortalActionChrome so the customer sees the tenant logo + project title
  // throughout the signing flow. Previously the page rendered a generic "🛡️
  // Document signing" header with no tenant or project context — and the
  // post-sign success card had no branding at all.
  const headerTitle =
    meta.review.project_name && meta.review.document_title
      ? `${meta.review.project_name} — ${meta.review.document_title}`
      : meta.review.project_name || meta.review.document_title || null;

  const branding = meta.branding ?? null;

  if (signedAt) {
    return (
      <PortalActionChrome apiBase={API_BASE} branding={branding} projectName={headerTitle}>
        <div className="flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-lg shadow border border-slate-200 p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold mb-1">Signature recorded</h1>
            <p className="text-sm text-slate-600 mb-4">
              Thank you, {meta.signer.email}. Your signature was recorded at{' '}
              {new Date(signedAt).toLocaleString()}.
            </p>
            <p className="text-xs text-slate-500">
              You can close this tab. The document owner has been notified.
            </p>
          </div>
        </div>
      </PortalActionChrome>
    );
  }

  return (
    <PortalActionChrome apiBase={API_BASE} branding={branding} projectName={headerTitle}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <section
          aria-label="Document preview"
          className="md:col-span-2 bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium">Document preview</span>
            {previewPages && previewPages.length > 0 ? (
              <span className="text-xs text-slate-500 ml-auto">
                {previewPages.length} page{previewPages.length === 1 ? '' : 's'}
                {previewTruncated ? ` of ${previewTotalCount} (first ${previewPages.length} shown)` : ''}
              </span>
            ) : null}
          </div>

          <div
            className="overflow-y-auto bg-slate-100 p-4 space-y-4"
            style={{ maxHeight: '70vh' }}
            data-testid="signing-page-preview"
          >
            {previewLoading ? (
              <div
                className="space-y-4"
                role="status"
                aria-label="Loading document preview"
              >
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="w-full bg-slate-200 rounded animate-pulse"
                    style={{ height: '60vh' }}
                  />
                ))}
              </div>
            ) : previewError ? (
              <div className="bg-white border border-amber-200 rounded p-6 text-center">
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-900">
                  Could not load document preview
                </p>
                <p className="text-xs text-slate-600 mt-1 mb-3">{previewError}</p>
                <p className="text-xs text-slate-500 mb-3">
                  You can still sign below, but please confirm with the sender what you are signing
                  before proceeding.
                </p>
                <Button
                  onClick={retryPreview}
                  variant="outline"
                  size="sm"
                  className="inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry preview
                </Button>
              </div>
            ) : previewPages && previewPages.length > 0 ? (
              <>
                {previewPages.map((page) => (
                  <div
                    key={page.page_number}
                    className="bg-white rounded shadow-sm border border-slate-200 overflow-hidden relative"
                  >
                    <div className="absolute top-2 right-2 z-10 bg-slate-900/75 text-white text-xs px-2 py-0.5 rounded">
                      Page {page.page_number}
                      {previewTruncated ? ` of ${previewTotalCount}` : previewPages.length > 1 ? ` of ${previewPages.length}` : ''}
                    </div>
                    <img
                      src={page.image_data_uri}
                      alt={`Document preview, page ${page.page_number} of ${previewTruncated ? previewTotalCount : previewPages.length}`}
                      className="w-full h-auto block"
                    />
                  </div>
                ))}
                {previewTruncated ? (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 text-center">
                    Showing first {previewPages.length} of {previewTotalCount} pages. Download the
                    full PDF to review all pages before signing if needed.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="bg-white border border-slate-200 rounded p-6 text-center text-sm text-slate-600">
                No preview available for this document.
              </div>
            )}
          </div>
        </section>

        <aside className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 space-y-4 self-start">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Signing as</p>
            <p className="text-sm font-medium text-slate-900">{meta.signer.email}</p>
            {meta.signer.role ? (
              <p className="text-xs text-slate-500">{meta.signer.role}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
              Typed name (optional)
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={meta.signer.email}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Optional. We'll use your email if you leave this blank.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Consent</p>
            <p className="text-xs text-slate-600 max-h-32 overflow-y-auto whitespace-pre-line">
              {meta.review.consent_text}
            </p>
            <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5"
              />
              <span>I have read and agree to the consent above.</span>
            </label>
          </div>

          {error ? (
            <p className="text-sm text-amber-600 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          ) : null}

          <Button
            onClick={handleSign}
            disabled={!consentChecked || signing}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {signing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Recording signature…
              </>
            ) : (
              'I confirm the above and sign'
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            Your IP, browser identity, and a timestamp are recorded for the audit trail.
          </p>
        </aside>
      </div>
    </PortalActionChrome>
  );
}

export default SigningPage;
